import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import encapsulatedStyles from './outline-container.inner.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

export const containerLayouts = [
  'default',
  '2-col',
  '70-30',
  '30-70',
  '20-80',
] as const;
export type ContainerLayout = (typeof containerLayouts)[number];

export const containerContentWidths = [
  'default',
  'full-bleed',
  'large',
  'full',
  'plus',
  'wide',
  'medium',
  'narrow',
] as const;
export type ContentWidths = (typeof containerContentWidths)[number];

/**
 * These are intended to be a direct passthrough of
 * the outline.theme.pcss spacing globals, and should not
 * need to be hooked up or altered on this component, rather
 * set in outline.theme.pcss
 */
export const containerSpaces = [
  'default',
  'space-0',
  'space-3xs',
  'space-2xs',
  'space-xs',
  'space-s',
  'space-m',
  'space-l',
  'space-xl',
  'space-2xl',
  'space-3xl',
  'space-4xl',
  'space-m-3xl',
  'space-xl-fluid',
  'space-2xl-fluid',
];
export type Space = (typeof containerSpaces)[number];

export const contentJustifications = [
  'flex-start',
  'flex-end',
  'center',
  'space-between',
  'space-around',
  'space-evenly',
] as const;
export type JustifyContent = (typeof contentJustifications)[number];

/**
 * Aside from transparent, these are just placeholders.
 * Update with your own brand/design system options.
 * Strive to match the Figma vars/tokens naming precisely
 * to maximize maintainability.
 */

export const backgroundColors = [
  'transparent',
  'primary-subtler',
  'brand-primary',
  'brand-secondary',
  'brand-neutral',
  'brand-boldest',
  'neutral-subtler',
] as const;
export type BackgroundColor = (typeof backgroundColors)[number];

/**
 * The OutlineContainer component
 * @element outline-container
 *
 * @slot - default
 * @slot - col-1 : For layouts 2-col 70-30 30-70 20-80
 * @slot - col-2 : For layouts 2-col 70-30 30-70 20-80
 *
 * @attr top-padding
 * @attr bottom-padding
 * @attr x-padding
 * @attr gap
 * @attr content-max-width
 * @attr justify-content
 * @attr background-color
 *
 * Creates a container component and accompanying set of utility
 * classes for import into other components. (Preferred over
 * placing components within other component's shadow roots.)
 *
 * The container component defines two areas, the container and
 * container wrapper. The container wrapper goes to the full bounds
 * of the host element. The container is the part which gets constrained
 * via padding, max-width, etc. This allows the creation of a full bleed
 * colored section with content of limited width.
 *
 * Note that many of the CSS vars used in this component's styles are
 * directly used globals, and are not further processed/derived.
 */
@customElement('outline-container')
export class OutlineContainer extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    // globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * The vertical space from the component above it, using
   * space variables defined in outline.theme.pcss.
   * Note that this sets the padding inside, so background
   * color will fill this space created.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'top-padding',
  })
  topPadding: Space;

  /**
   * Same as top-padding but for the bottom. Background color
   * will fill this space created.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'bottom-padding',
  })
  bottomPadding: Space;

  /**
   * The horizontal (left/right) padding of the component.
   * Background color will fill this space as well. Note that
   * this works with the content-max-width value to ensure that,
   * at maximum width, content with different padding values will
   * all align vertically assuming the same content-max-width value.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'x-padding',
  })
  xPadding: Space = 'default';

  /**
   * The vertical (top/bottom) padding of the component.
   * Background color will fill this space as well. Note that
   * this will be overridden if top-padding or bottom-padding values are set.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'y-padding',
  })
  yPadding: Space = 'default';

  /**
   * Container content is displayed vertically using flex-column.
   * This gap represents the space between individual items placed
   * in the container.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'gap',
  })
  gap: Space = 'space-0';

  /**
   * Sets the width limit for the container's content.
   *
   * In CSS this property interacts with the xPadding attribute
   * to ensure that elements align when they hit their limits.
   * max-width: calc(
   *   var(--container--content-max-width) + 2 * var(--container--x-padding)
   * );
   *
   * The 'full' value is meant to represent your design's maximum
   * content width, whereas 'unrestricted' is intended to remove the
   * max-width limitation entirely.
   *
   * Background color goes into the container wrapper, so it has no
   * width restriction.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'content-max-width',
  })
  contentMaxWidth: ContentWidths = 'default';

  /**
   * Container content is displayed vertically using flex-column.
   * Justify content does exactly what it says.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'justify-content',
  })
  justifyContent: JustifyContent = 'flex-start';

  /**
   * Sets the background color for the container wrapper.
   * The critical distinction here is that this goes all the way
   * to the edge of the parent element, and is not constrained by
   * content-max-width and will fill the space created with padding.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'background-color',
  })
  backgroundColor: BackgroundColor = 'transparent';

  /**
   * Sets container layout.
   * 2-col: 50/50 split
   * 70-30: 70% 30% split
   * 30-70: 30% 70% split
   * 20-80: 20% 75% split
   * Defaults to 1 column.
   */
  @property({
    type: String,
    reflect: true,
    attribute: 'layout',
  })
  layout: ContainerLayout = 'default';

  render(): TemplateResult {
    const containerWrapperClasses = {
      'container-wrapper': true,
      [`container-wrapper--background-color--${this.backgroundColor}`]: true,
    };

    const containerClasses = {
      container: true,
      [`container--top-padding--${this.topPadding}`]: !!this.topPadding,
      [`container--bottom-padding--${this.bottomPadding}`]:
        !!this.bottomPadding,
      [`container--y-padding--${this.yPadding}`]:
        this.topPadding || this.bottomPadding ? false : true,
      [`container--x-padding--${this.xPadding}`]: true,
      [`container--gap--${this.gap}`]: true,
      [`container--content-max-width--${this.contentMaxWidth}`]: true,
      [`container--justify-content--${this.justifyContent}`]: true,
    };

    return html`
      <div class="${classMap(containerWrapperClasses)}">
        <div class="${classMap(containerClasses)}">
          ${this.layoutTemplate()}
        </div>
      </div>
    `;
  }

  layoutTemplate() {
    if (this.layout !== 'default') {
      return html`
        <div class="columns-container">
          <div class="col-1-wrapper">
            <slot name="col-1"></slot>
          </div>
          <div class="col-2-wrapper print-hide">
            <slot name="col-2"></slot>
          </div>
        </div>
      `;
    } else {
      return html`<slot></slot>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-container': OutlineContainer;
  }
}
